import React, { useState, useEffect } from "react";
import Header from "../../Header";
import Footer from "../../Footer";
import { Link, useParams, useNavigate} from "react-router-dom";
import config from "../../common/Config";
import { toast } from "sonner";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid2,
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import '../../common/commonStyles.css';
import { encryptPayload, decryptPayload } from '../../../utils/customerCrypto';
import { getCompanySettings, resolveCustomerApiEncrypt } from '../../../utils/companySettingsCache';

const Contra_Price_Edit = () => {
  const [tableRows, setTableRows] = useState([]);
  const [data, setData] = useState([]);
  const accessToken = localStorage.getItem("accessToken");
  const id  = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigateToContraList = useNavigate();
  const token = localStorage.getItem("token");
  
  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchProductData();
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    const formData = new FormData();
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    formData.append("id", id.customerId);
    if (useEncrypt) {
      const enc = encryptPayload({ id: id.customerId });
      if (enc) {
        formData.append("encrypted_payload", enc);
      }
    }
    const apiUrl = `${config.API_URL}/ws_get_customer_details_id`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch customer details");
      }
      const body = await response.json();
      const resolved = useEncrypt
        ? (decryptPayload(body?.encrypted_payload) ||
            decryptPayload(body?.encrypted_data) ||
            decryptPayload(body?.payload) ||
            body)
        : body;
      setData(resolved?.data);
    } catch (error) {
      console.error("Error fetching customer details:", error);
      toast.error("Failed to fetch customer details");
    }
  };

  const fetchProductData = async () => {
    const formData = new FormData();
    formData.append("id", id.customerId);
    const apiUrl = `${config.API_URL}/contra_price_edit`;
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch product data");
      }
      const data = await response.json();
      setTableRows(data.data || []);
    } catch (error) {
      console.error("Error fetching product data:", error);
      toast.error("Failed to fetch product data");
    }
  };

  const handleRemoveRow = async (rowId) => {
    const formData = new FormData();
    formData.append("id", rowId);
    const apiUrl = `${config.API_URL}/contra_price_delete`;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to remove row from the server.");
      }

      const data = await response.json();
      if (data.success) {
        toast.success("Product removed successfully.");
        const updatedRows = tableRows.filter((row) => row.id !== rowId);
        setTableRows(updatedRows);
      } else {
        toast.error('Failed to remove product.');
      }
    } catch (error) {
      console.error("Error during API call:", error);
      toast.error("An error occurred while trying to remove the row.");
    }
  };

  const submitFormData = async () => {
    if (tableRows.length < 1) {
      toast.error('Please add a product.');
      return;
    }

    setIsSubmitting(true);
    const form = document.getElementById('editcontraform');
    const formData = new FormData(form);
    let a = formData.getAll('product_price[]');
    let nonEmptyPrices = a.filter(price => price.trim() !== '');

    if(nonEmptyPrices.length !== tableRows.length) {
      toast.error('Please enter price for each product.');
      setIsSubmitting(false);
      return;
    }
    formData.append('customer_id', id.customerId);
    const apiSalesRouteUrl = `${config.API_URL}/contra_price_update`;
    try {
      const response = await fetch(apiSalesRouteUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if(data.success) {
        toast.success("Contra price updated successfully.");
        navigateToContraList(`/ContraList`);
      } else {
        toast.error(data.message || "Failed to update contra price");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('api fail error fetching products', error);
      toast.error("Failed to update contra price");
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box className="page-container">
          <Paper className="paper-style">
            <Box sx={{ px: 2, py: 1.2 }}>
              <Grid2 container spacing={2} alignItems="center">
                <Grid2 size={{ xs: 12, sm: 6, md: 8 }}>
                  <Typography className="page-title">
                    Contra Price Edit
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <TextField
                    fullWidth
                     className="input-border"
                    size="small"
                    label="Customer Name"
                    name="customer"
                    value={data.company || ''}
                    disabled
                  />
                </Grid2>
              </Grid2>
            </Box>
            <TableContainer>
              <form id="editcontraform">
                <Table
                  size="medium"
                  className="table-cell-border"
                  sx={{ tableLayout: "fixed"}}
                >
                  <TableHead>
                    <TableRow className="table-cell-header">
                      {["Sr. No", "Product Name", "Price", "Action"].map((header) => {
                        let align = "left";
                        if (header === "Sr. No" || header === "Action") {
                          align = "center";
                        }
                        return (
                          <TableCell
                            key={header}
                            align={align}
                            sx={{
                              width: header === "Sr. No" ? '80px' : header === "Product Name" ? '200px' : header === "Price" ? '120px' : header === "Action" ? '120px' : 'auto',
                            }}
                          >
                            {header}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No product added
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableRows.map((row, i) => (
                        <TableRow
                          key={row.id}
                          hover
                          className="table-cell-style"
                        >
                          <TableCell align="center" className="table-cell">
                            {i + 1}
                            <input type="hidden" name="product_id[]" value={row.product_id} />
                            <input type="hidden" name="id[]" value={row.id} />
                          </TableCell>
                          <TableCell align="left" >
                            {row.name} ({row.code})
                          </TableCell>
                          <TableCell align="left" >
                            <TextField
                              type="text"
                              name="product_price[]"
                              defaultValue={row.price}
                              size="small"
                              fullWidth
                              inputProps={{
                                style: {
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: 'rgba(145, 158, 171, 0.32)',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'rgba(145, 158, 171, 0.48)',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#007bff',
                                  },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() => handleRemoveRow(row.id)}
                              size="small"
                              sx={{
                                color: '#637381',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 0, 0, 0.08)',
                                  color: '#d32f2f',
                                },
                              }}
                            >
                              <Iconify icon="eva:trash-2-outline" width={20} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </form>
            </TableContainer>
            <Box sx={{ p:2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                className="button-variant-contained"
                size="small"
                onClick={submitFormData}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>
          </Paper>
      </Box>
    </>
  );
};

export default Contra_Price_Edit;
